import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { Box, Grid, InputAdornment, Typography } from '@mui/material'
import PromotionsApi from 'api/promotion/promotion.api'
import { resetModal } from 'app/slice/modal.slice'
import Loading from 'component/Loading'
import TableRowExport from 'component/Pdf/TableRowExport'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'

import MainButton from 'new-components/buttons/main-button'
import TextInput from 'new-components/inputs/text-input'
import AlertModal from 'new-components/modal/alert-modal'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import { notiError, notiSuccess } from 'component/notifications/notifications'
import DropdownButton from 'new-components/buttons/dropdown-button'
import SwitchButton from 'new-components/buttons/switch-button'
import SelectInput from 'new-components/inputs/select-input'
import PageModal from 'new-components/modal/page-modal'
import { dateTimeToView } from 'utils/date.utils'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'
import { exportPromotionTypeToExcel } from './exportPromotionTypeToExcel'

const permissions = {
    isCreate: isCreate(),
    isDelete: isDelete(),
    isUpdate: isUpdate(),
    isView: isView()
}

const PromotionType = () => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const componentRef = useRef<any>(null)
    const [load, setLoad] = useState<boolean>(false)
    const [dataPromotion, setDataPromotion] = useState<any>([])
    const [dataPromotionExport, setDataPromotionExport] = useState<any>([])

    const [statusSelect, setStatusSelect] = useState<string>('ALL')

    const [page, setPage] = useState(1)
    const [pageLimit, setPageLimit] = useState(10)
    const [rowCount, setRowCount] = useState(0)
    const [sortBy, setSortBy] = useState('promotionTypeId')
    const [sortType, setSortType] = useState('ASC')
    const [search, setSearch] = useState('')

    const [isOpen, setIsOpen] = useState(false)
    const [promotionTypeId, setPromotionTypeId] = useState<number>(0)
    const [promotionTypeStatus, setPromotionTypeStatus] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE')
    const [status, setStatus] = useState<any>('')
    const [promotionTypeName, setPromotionTypeName] = useState<string>('')
    const [promotionTypeNameEn, setPromotionTypeNameEn] = useState<string>('')
    const [errorMessage, setErrorMessage] = useState<any>({})
    const [statusSwitch, setStatusSwitch] = useState(true)
    const [alertModal, setAlertModal] = useState(false)
    const [disabledSubmit, setDisabledSubmit] = useState(false)

    const preparation = async (searchData?: string, checkExport?: boolean) => {
        setLoad(true)
        const condition = {
            page: page,
            pageLimit: pageLimit,
            SEARCH: searchData === '' ? '' : search,
            sortBy: sortBy,
            sortType: sortType,
            STATUS: statusSelect === 'ALL' ? '' : statusSelect,
            EXPORT: checkExport ? '1' : '0'
        }
        await PromotionsApi.findAllType(condition).then((resp) => {
            if (resp.status === 200) {
                if (checkExport) {
                    setDataPromotionExport(resp.data)
                } else {
                    setRowCount(resp?.headers['x-total'] || 0)
                    setDataPromotion(resp.data)
                }
            }
        })

        setLoad(false)
    }

    useEffect(() => {
        preparation()
        return () => {
            setPromotionTypeId(0)
            setPromotionTypeName('')
            setPromotionTypeNameEn('')
            setErrorMessage({})
        }
    }, [page, pageLimit, sortBy, sortType])

    useEffect(() => {
        preparation('', true)
    }, [])

    const onEdit = (obj: any) => {
        setIsOpen(true)
        setPromotionTypeId(obj.promotionTypeId)
        setStatusSwitch(obj.status === 'INACTIVE' ? false : true)
        setPromotionTypeName(obj.promotionTypeName)
        setPromotionTypeNameEn(obj.promotionTypeNameEn)
    }

    const onActive = async () => {
        if (promotionTypeId) {
            PromotionsApi.chagneStatusPromotionType(promotionTypeId, status).then((resp) => {
                if (resp.status === 200) {
                    setAlertModal(false)
                    notiSuccess(t('PROMOTION_TYPE.MESSAGE.SUCCESS'))
                    preparation(search, true)
                    preparation()
                } else {
                    setAlertModal(false)
                    notiError(t('PROMOTION_TYPE.MESSAGE.ERROR_'))
                }
            }).catch((error) => {
                setAlertModal(false)
                if (error?.response?.data?.message === 'THIS_PROMOTION_TYPE_IS_BEING_USED') {
                    notiError(t('PROMOTION_TYPE.MESSAGE.ERROR.THIS_PROMOTION_TYPE_IS_BEING_USED'))
                } else {
                    notiError(t('PROMOTION_TYPE.MESSAGE.ERROR_'))
                }
            })
        }
    }

    const onDelete = async () => {
        if (promotionTypeId) {
            PromotionsApi.chagneStatusPromotionType(promotionTypeId, "DELETED").then((resp) => {
                if (resp.status === 200) {
                    setAlertModal(false)
                    notiSuccess(t('PROMOTION_TYPE.MESSAGE.DEL_SUCCESS'))
                    preparation(search, true)
                    preparation()
                } else {
                    setAlertModal(false)
                    notiError(t('PROMOTION_TYPE.MESSAGE.ERROR_'))
                }
            }).catch((error) => {
                const errMsg = error?.response?.data?.message
                if (errMsg === "THIS_PROMOTION_TYPE_IS_BEING_USED") {
                    notiError(t('PROMOTION_TYPE.MESSAGE.ERROR.THIS_PROMOTION_TYPE_IS_BEING_USED'))
                } else {
                    notiError(t('PROMOTION_TYPE.MESSAGE.ERROR_'))
                }
            })
        }
    }

    const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
        setSortType(sortTypeVal)
        setSortBy(sortByVal)
    }

    const handleChangePage = (val: number) => setPage(val)

    const handleChangeRowsPerPage = (limit: number) => setPageLimit(limit)

    const headCells = [
        { id: 'no', disablePadding: false, label: t('TABLE.NO'), width: '50px', align: 'center', },
        { id: 'promotionTypeName', disablePadding: false, label: t('PROMOTION_TYPE.TABLE.PROMOTION_TYPENAME'), align: 'left', },
        { id: 'promotionTypeNameEn', disablePadding: false, label: t('PROMOTION_TYPE.TABLE.PROMOTION_TYPENAMEEN'), align: 'left', },
        { id: 'status', disablePadding: false, label: t('APPLICATION.TABLE.CELL.STATUS'), align: 'center', width: '150px' },
        { id: 'updatedBy', disablePadding: false, label: t('APPLICATION.TABLE.CELL.UPDATED_BY'), align: 'center', width: '180px' },
        { id: 'action', disablePadding: false, label: 'Action', width: '80px' }
    ]

    const renderData = (objData: any, index: number, isExport?: boolean) => {
        if (isExport) {
            index = index + 1
        } else {
            index = page * pageLimit - pageLimit + index + 1
        }
        const { promotionTypeId, promotionTypeName, promotionTypeNameEn, updatedBy, updatedAt, createdAt, status } = objData

        const objRenderData = {
            key: promotionTypeId,
            id: promotionTypeId,
            obj: objData,
            columns: [
                { option: 'TEXT', align: 'center', label: index },
                { option: 'TEXT', align: 'left', label: promotionTypeName || '' },
                { option: 'TEXT', align: 'left', label: promotionTypeNameEn || '' },
                { option: 'STATUS', align: 'center', label: status },
                { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy || '-', updatedAt: updatedAt ?? '10/12/2564 08.59' } },
                {
                    option: 'ACTION',
                    align: 'center',
                    label: 'action',
                    values: [
                        { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: status === 'ACTIVE' },
                        { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: status === 'INACTIVE' },
                        { option: 'DIVIDER', label: '', disabled: false },
                        { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
                        { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
                    ]
                },
            ]
        }

        if (isExport) {
            return <TableRowExport {...objRenderData} />
        }

        return (
            <TableRowCommon
                {...objRenderData}
                onedit={() => onEdit(objData)}
                ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETED', objRenderData.obj)}
                onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
                oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
            />
        )
    }

    const clearErrorMessage = () => {
        setErrorMessage({})
    }

    const resetForm = () => {
        setPromotionTypeId(0)
        setPromotionTypeName('')
        setPromotionTypeNameEn('')
        setPromotionTypeStatus('ACTIVE')
        clearErrorMessage()
    }

    const handleSubmitModal = (id: number, status: string, obj?: any) => {
        if (status === 'DELETED') {
            setStatus("DELETED")
            setPromotionTypeId(obj.promotionTypeId)
            setPromotionTypeName(obj.promotionTypeName)
            setPromotionTypeNameEn(obj.promotionTypeNameEn)
            setAlertModal(true)
        } else {
            setAlertModal(true)
            setPromotionTypeId(id)
            setStatus(status)
            setPromotionTypeName(obj.promotionTypeName)
            setPromotionTypeNameEn(obj.promotionTypeNameEn)
        }
    }

    const onSubmit = async () => {
        setLoad(true)
        setDisabledSubmit(true)
        if (!promotionTypeName || !promotionTypeNameEn) {

            setErrorMessage({
                PROMOTIONTYPENAME: !promotionTypeName && t('PROMOTION_TYPE.MESSAGE.ERROR.PROMOTION_TYPENAME'),
                PROMOTIONTYPENAMEEN: !promotionTypeNameEn && t('PROMOTION_TYPE.MESSAGE.ERROR.PROMOTION_TYPENAMEEN')
            })
            setDisabledSubmit(false)
            setLoad(false)
            return
        }

        if (promotionTypeName?.length > 255 || promotionTypeNameEn?.length > 255) {
            notiError(t('PROMOTION_TYPE.MESSAGE.ERROR.MAX_LENGTH'))
            setDisabledSubmit(false)
            setLoad(false)
            return
        }

        if (promotionTypeId) {
            const newPayload = {
                promotionTypeName: promotionTypeName,
                promotionTypeNameEn: promotionTypeNameEn,
                status: statusSwitch ? 'ACTIVE' : 'INACTIVE',
            }
            await PromotionsApi.updatePromotionType(newPayload, promotionTypeId).then((res) => {
                setIsOpen(false)
                setDisabledSubmit(false)
                notiSuccess(t('PROMOTION_TYPE.MESSAGE.EDIT_SUCCESS'))
                dispatch(resetModal())
                resetForm()
                preparation()
            }).catch((error) => {
                setDisabledSubmit(false)
                const errMsg = error?.response?.data?.message
                if (errMsg) {
                    if (errMsg === "THIS_PROMOTION_TYPE_IS_BEING_USED") {
                        notiError(t('PROMOTION_TYPE.MESSAGE.ERROR.THIS_PROMOTION_TYPE_IS_BEING_USED'))
                        return
                    }
                    setErrorMessage({
                        PROMOTIONTYPENAME: errMsg === "IS_DUPLICATE_TYPE_NAME" && t('PROMOTION_TYPE.MESSAGE.ERROR.IS_DUPLICATE_TYPE_NAME'),
                        PROMOTIONTYPENAMEEN: errMsg === "IS_DUPLICATE_TYPE_NAME_EN" && t('PROMOTION_TYPE.MESSAGE.ERROR.IS_DUPLICATE_TYPE_NAME_EN')
                    })
                } else {
                    notiError(t('PROMOTION_TYPE.MESSAGE.ERROR_'))
                }

            }).finally(() => {
                setLoad(false)
                setDisabledSubmit(false)
            })
        } else {
            const newPayload = {
                promotionTypeName: promotionTypeName,
                promotionTypeNameEn: promotionTypeNameEn
            }
            await PromotionsApi.createPromotionType(newPayload).then((res) => {
                setIsOpen(false)
                setDisabledSubmit(false)
                notiSuccess(t('PROMOTION_TYPE.MESSAGE.SAVE_SUCCESS'))
                dispatch(resetModal())
                resetForm()
                preparation()
            }).catch((error) => {
                setDisabledSubmit(false)
                const errMsg = error?.response?.data?.message
                if (errMsg) {
                    if (errMsg === "THIS_PROMOTION_TYPE_IS_BEING_USED") {
                        notiError(t('PROMOTION_TYPE.MESSAGE.ERROR.THIS_PROMOTION_TYPE_IS_BEING_USED'))
                        return
                    }
                    setErrorMessage({
                        PROMOTIONTYPENAME: errMsg === "IS_DUPLICATE_TYPE_NAME" && t('PROMOTION_TYPE.MESSAGE.ERROR.IS_DUPLICATE_TYPE_NAME'),
                        PROMOTIONTYPENAMEEN: errMsg === "IS_DUPLICATE_TYPE_NAME_EN" && t('PROMOTION_TYPE.MESSAGE.ERROR.IS_DUPLICATE_TYPE_NAME_EN')
                    })
                } else {
                    notiError(t('PROMOTION_TYPE.MESSAGE.ERROR_'))
                }

            }).finally(() => {
                setLoad(false)
                setDisabledSubmit(false)
            })
        }
    }

    return (
        <Box>
            <Loading show={load} />
            <AlertModal
                isOpen={alertModal}
                title={status === "DELETED" ? t('PROMOTION_TYPE.MESSAGE.DEL_TITLE') : t('PROMOTION_TYPE.MESSAGE.STATUS_TITLE')}
                message={status === "DELETED" ? t('PROMOTION_TYPE.MESSAGE.DELETE_MESSAGE', { promotionTypeName: promotionTypeName, promotionTypeNameEn: promotionTypeNameEn }) : t('PROMOTION_TYPE.MESSAGE.STATUS_MESSAGE', { name: promotionTypeName, status: t(`STATUS.${status}`) })}
                type={status === "DELETED" ? 'error' : 'warning'}
                onSubmit={{ onClick: status === 'DELETED' ? onDelete : onActive, title: t('PROMOTION_TYPE.MESSAGE.BUTTON_SAVE_DELETE') }}
                onCancel={{
                    onClick: () => {
                        setAlertModal(false)
                    }, title: t('PROMOTION_TYPE.MESSAGE.BUTTON_CANCEL')
                }}
                handleClose={() => {
                    setAlertModal(false)
                }}
            />
            <PageModal
                isOpen={isOpen}
                title={`${promotionTypeId !== 0 ? t('PROMOTION_TYPE.TITLE_UPDATE') : t('PROMOTION_TYPE.TITLE_CREATE')}`}
                size="XS"
                onSubmit={{ onClick: onSubmit, title: t('TREATMENT_COURSE_TYPE.BUTTON.SAVE'), disabled: disabledSubmit }}
                onCancel={{
                    onClick: () => {
                        resetForm()
                        setIsOpen(false)
                    }, title: t('TREATMENT_COURSE_TYPE.BUTTON.CANCEL')
                }}
                handleClose={() => {
                    resetForm()
                    setIsOpen(false)
                }}
            >
                <Box marginTop="16px">
                    {promotionTypeId !== 0 && <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="16px">
                        <Typography fontSize="16px" fontWeight={400}>{t('PROMOTION_TYPE.MODAL.STATUS')}</Typography>
                        <SwitchButton checked={statusSwitch} onChange={(e) => setStatusSwitch(e.target.checked)} />
                    </Box>}
                    <Box display="flex" flexDirection="column" gap="16px">
                        <TextInput
                            required
                            value={promotionTypeName}
                            inputProps={{ value: promotionTypeName }}
                            label={t('PROMOTION_TYPE.TABLE.PROMOTION_TYPENAME')}
                            helperText={
                                errorMessage.PROMOTIONTYPENAME ||
                                errorMessage.IS_DUPLICATE_PROMOTIONTYPENAME
                            }
                            onChange={(e) => {
                                setErrorMessage({})
                                setPromotionTypeName(e.target.value)

                            }}
                            fullWidth
                        />
                        <TextInput
                            helperText={
                                errorMessage.PROMOTIONTYPENAMEEN ||
                                errorMessage.IS_DUPLICATE_PROMOTIONTYPENAMEEN
                            }
                            label={t('PROMOTION_TYPE.TABLE.PROMOTION_TYPENAMEEN')}
                            required
                            value={promotionTypeNameEn}
                            inputProps={{ value: promotionTypeNameEn }}
                            onChange={(e) => setPromotionTypeNameEn(e.target.value)}
                            fullWidth
                        />
                    </Box>
                </Box>
            </PageModal>
            <Box width="100%" height="80px" boxShadow="0px 3px 6px 0px #0000001F" border="1px solid #E5E5E5" paddingLeft="24px" display="flex" alignItems="center" fontSize="20px" fontWeight={500}  >
                {t('PROMOTION_TYPE.TITLE')}
            </Box>
            <Box paddingX="24px" paddingTop="16px">
                <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <TextInput
                            onChange={(event) => {
                                setSearch(event.target.value);
                            }}
                            label={t('PROMOTION_TYPE.SEARCH')}
                            value={search}
                            size='large'
                            fullWidth={true}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    setPage(1)
                                    preparation(search, true)
                                    preparation()
                                }
                            }}
                            inputProps={{
                                value: search,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                                            <FontAwesomeIcon
                                                icon={faTimes}
                                                onClick={() => {
                                                    setSearch('')
                                                }}
                                                style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <SelectInput
                            value={statusSelect}
                            onChange={(event: any) => {
                                setStatusSelect(event.target.value)
                            }}
                            defaultValue={statusSelect}
                            label="สถานะ"
                            options={[
                                { name: t('VOUCHER_TYPES.STATUS.ALL'), value: "ALL" },
                                { name: t('VOUCHER_TYPES.STATUS.ACTIVE'), value: "ACTIVE" },
                                { name: t('VOUCHER_TYPES.STATUS.INACTIVE'), value: "INACTIVE" }
                            ]}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} xl={8}>
                        <Box display={'flex'} justifyContent={'space-between'}>
                            <MainButton
                                title={t('INPUT.SEARCH')}
                                type='primary'
                                variant='contained'
                                style={{ width: '120px' }}
                                onClick={() => {
                                    setPage(1)
                                    preparation(search, true)
                                    preparation()
                                }} />

                            <Box display="flex" flexDirection="row" alignItems="center" gap="16px">
                                <DropdownButton title='พิมพ์เอกสาร' onChange={(e) => {
                                    if (e === 'Excel') {
                                        const report: any = {
                                            title: 'ประเภทโปรโมชัน',
                                            statusFilter: statusSelect === 'ALL'
                                                ? 'ทั้งหมด'
                                                : statusSelect === 'ACTIVE'
                                                    ? 'เปิดใช้งาน'
                                                    : 'ปิดใช้งาน',
                                            rows: dataPromotionExport.map((item: any) => ({
                                                promotionTypeName: item.promotionTypeName || '-',
                                                promotionTypeNameEn: item.promotionTypeNameEn || '-',
                                                status: t(`STATUS.${item.status}`),
                                                updatedBy: item.updatedBy || '-',
                                                updatedAt: dateTimeToView(item.updatedAt)
                                            })),
                                            t
                                        }
                                        exportPromotionTypeToExcel(report)
                                    } else if (e === 'PDF') {
                                        const style = document.createElement("style");
                                        style.innerHTML = `
                      @media print {
                        @page { size: portrait }
                      }
                    `;
                                        document.head.appendChild(style);
                                        window.print();
                                        document.head.removeChild(style);

                                    }
                                }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} />
                                <MainButton
                                    onClick={() => {
                                        setPromotionTypeId(0)
                                        setIsOpen(true)
                                    }}
                                    title={t('PROMOTION_TYPE.TITLE_CREATE')}
                                    type="primary"
                                    variant="contained"
                                    startIcon={<AddCircleIcon />}
                                    style={{ height: '40px' }}
                                    disabled={permissions.isCreate.disabled}
                                />
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
                <Box marginTop="24px">
                    <TableCustom
                        page={page}
                        pageLimit={pageLimit}
                        sortType={sortType}
                        sortBy={sortBy}
                        onSort={onRequestSort}
                        setPageLimit={handleChangeRowsPerPage}
                        setPage={handleChangePage}
                        rowCount={rowCount}
                        headCells={headCells}
                        rowsData={dataPromotion?.length ? dataPromotion?.map((item: any, index: number) => {
                            return renderData(item, index)
                        }) : []}
                    />

                    <Box
                        id="print-table-container"
                        ref={componentRef}
                    >
                        <Typography fontSize="14px" fontWeight={400}>{t('PROMOTION_TYPE.TITLE')}</Typography>
                        <Box display='flex' flexDirection="row" alignItems="center" gap="16px">
                            <Typography fontSize="10px" fontWeight={400}>{t('STATUS.LABEL')}: {statusSelect === 'ALL' ? t('STATUS.ALL') : statusSelect === 'ACTIVE' ? t('STATUS.ACTIVE') : t('STATUS.INACTIVE')}</Typography>
                        </Box>
                        <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse', marginTop: '16px' }}>
                            <thead>
                                <tr style={{ textAlign: 'center', fontSize: '9.5px', fontWeight: 700 }}>
                                    <th style={{ borderBottom: '1px solid black' }}>{t('#')}</th>
                                    <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('PROMOTION_TYPE.TABLE.PROMOTION_TYPENAME')}</th>
                                    <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('PROMOTION_TYPE.TABLE.PROMOTION_TYPENAMEEN')}</th>
                                    <th style={{ borderBottom: '1px solid black' }}>{t('APPLICATION.TABLE.CELL.STATUS')}</th>
                                    <th style={{ borderBottom: '1px solid black' }}>{t('APPLICATION.TABLE.CELL.UPDATED_BY')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dataPromotionExport?.length === 0 &&
                                    <tr>
                                        <td colSpan={5} style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '100%' }}>{t('NOT_FOUND')}</td>
                                    </tr>
                                }
                                {dataPromotionExport?.map((data: any, index: number) => (
                                    <tr key={index}>
                                        <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '50px' }}>{index + 1}</td>
                                        <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center' }}>{data?.promotionTypeName || '-'}</td>
                                        <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center' }}>{data?.promotionTypeNameEn || '-'}</td>
                                        <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '50px' }}>{t(`STATUS.${data?.status}`) || '-'}</td>
                                        <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                            <span>{data?.updatedBy || '-'}</span>
                                            <span>{dateTimeToView(data?.updatedAt) || '-'}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export default PromotionType